"use server";

import { NewsletterSchema } from "@/lib/schemas";

export type NewsletterErrorCode =
  | "missingFormData"
  | "invalidEmail"
  | "serverConfig"
  | "backend"
  | "network";

type NewsletterResult =
  | { success: true; error?: never }
  | { success?: never; error: NewsletterErrorCode };

export async function subscribeToNewsletter(
  prevStateOrFormData: unknown,
  maybeFormData?: FormData,
): Promise<NewsletterResult> {
  // Supports both useActionState (prevState, formData) and direct calls (formData)
  const formData = prevStateOrFormData instanceof FormData ? prevStateOrFormData : maybeFormData;

  if (!(formData instanceof FormData)) {
    console.error("No FormData provided to subscribeToNewsletter");
    return { error: "missingFormData" };
  }

  const email = formData.get("email");
  const validated = NewsletterSchema.safeParse({ email });

  if (!validated.success) {
    console.error("Validation failed:", validated.error.format());
    return { error: "invalidEmail" };
  }

  const backendUrl = process.env.BACKEND_GRAPHQL_URL;
  const apiKey = process.env.BACKEND_API_KEY;

  if (!backendUrl || !apiKey) {
    console.error("Missing environment variables:", {
      backendUrl: backendUrl ? "defined" : "undefined",
      apiKey: apiKey ? "defined" : "undefined"
    });
    return { error: "serverConfig" };
  }

  try {
    const response = await fetch(backendUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-KEY": apiKey,
      },
      body: JSON.stringify({
        query: `
          mutation subscribeNewsletter($email: String!) {
            subscribeNewsletterContact(input: { email: $email }) {
              contact { id }
            }
          }
        `,
        variables: { email: validated.data.email },
      }),
    });

    const result = await response.json();

    if (!response.ok || result.errors) {
      console.error("Backend error:", {
        status: response.status,
        errors: result.errors,
      });
      return { error: "backend" };
    }

    return { success: true };
  } catch (e) {
    console.error("Fetch error:", e);
    return { error: "network" };
  }
}
